import { useState } from 'react'
import { VestingMilestone, VestingStatus } from '@/lib/vesting-calculator'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Progress } from '@/components/ui/progress'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { 
  Calendar,
  Clock,
  TrendingUp,
  AlertCircle,
  CheckCircle2,
  Zap,
  ChevronRight,
  Award
} from 'lucide-react'
import { format, differenceInDays, isAfter, parseISO } from 'date-fns'
import { cn } from '@/lib/utils'

interface VestingTimelineProps {
  status: VestingStatus
  milestones: VestingMilestone[]
  showAcceleration?: boolean
  onSimulateAcceleration?: () => void
}

export default function VestingTimeline({
  status,
  milestones,
  showAcceleration = false,
  onSimulateAcceleration
}: VestingTimelineProps) {
  const [expandedView, setExpandedView] = useState(false)
  
  const today = new Date()
  const nextMilestone = milestones.find(m => !m.is_completed)
  const daysUntilNext = nextMilestone 
    ? differenceInDays(parseISO(nextMilestone.date), today)
    : null

  // Group milestones by year for compact view
  const milestonesByYear = milestones.reduce((acc, milestone) => {
    const year = new Date(milestone.date).getFullYear()
    if (!acc[year]) acc[year] = []
    acc[year].push(milestone)
    return acc
  }, {} as Record<number, VestingMilestone[]>)

  const formatShareNumber = (shares: number) => {
    return new Intl.NumberFormat('en-US').format(shares)
  }

  return (
    <div className="space-y-4">
      {/* Summary Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">Vesting Progress</CardTitle>
            <div className="flex items-center gap-2">
              {status.cliff_reached ? (
                <Badge variant="default" className="bg-green-100 text-green-800">
                  <CheckCircle2 className="h-3 w-3 mr-1" />
                  Cliff Passed
                </Badge>
              ) : (
                <Badge variant="secondary" className="bg-yellow-100 text-yellow-800">
                  <Clock className="h-3 w-3 mr-1" />
                  Pre-Cliff
                </Badge>
              )}
              {status.is_fully_vested && (
                <Badge variant="default" className="bg-purple-100 text-purple-800">
                  <Award className="h-3 w-3 mr-1" />
                  Fully Vested
                </Badge>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Progress Bar */}
            <div>
              <div className="flex justify-between text-sm text-gray-600 mb-2">
                <span>{formatShareNumber(status.vested_shares)} vested</span>
                <span>{formatShareNumber(status.unvested_shares)} unvested</span>
              </div>
              <Progress value={status.vesting_percentage} className="h-3" />
              <div className="flex justify-between text-sm mt-2">
                <span className="font-medium">{status.vesting_percentage.toFixed(1)}%</span>
                <span className="text-gray-500">
                  {formatShareNumber(status.total_shares)} total shares
                </span>
              </div>
            </div>

            {/* Key Dates */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-2">
              <div>
                <p className="text-sm text-gray-500">Grant Date</p>
                <p className="font-medium">{format(parseISO(status.grant_date), 'MMM d, yyyy')}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Cliff Date</p>
                <p className="font-medium">{format(parseISO(status.cliff_date), 'MMM d, yyyy')}</p>
                {!status.cliff_reached && status.days_until_cliff && (
                  <p className="text-xs text-yellow-600">{status.days_until_cliff} days</p>
                )}
              </div>
              <div>
                <p className="text-sm text-gray-500">Next Vesting</p>
                <p className="font-medium">
                  {status.next_vesting_date 
                    ? format(parseISO(status.next_vesting_date), 'MMM d, yyyy')
                    : 'Complete'}
                </p>
                {daysUntilNext && (
                  <p className="text-xs text-blue-600">{daysUntilNext} days</p>
                )}
              </div>
              <div>
                <p className="text-sm text-gray-500">End Date</p>
                <p className="font-medium">{format(parseISO(status.vesting_end_date), 'MMM d, yyyy')}</p>
              </div>
            </div>

            {/* Acceleration Option */}
            {showAcceleration && !status.is_fully_vested && (
              <div className="flex items-center justify-between pt-2 border-t">
                <div className="flex items-center text-sm text-gray-600">
                  <Zap className="h-4 w-4 mr-2 text-yellow-500" />
                  Simulate accelerated vesting scenarios
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onSimulateAcceleration}
                >
                  <TrendingUp className="h-4 w-4 mr-2" />
                  Simulate
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Timeline View */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">Vesting Timeline</CardTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setExpandedView(!expandedView)}
            >
              {expandedView ? 'Compact View' : 'Detailed View'}
              <ChevronRight className={cn(
                "h-4 w-4 ml-1 transition-transform",
                expandedView && "rotate-90"
              )} />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {expandedView ? (
            /* Detailed Timeline */
            <div className="relative">
              <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200"></div>
              
              {milestones.map((milestone, index) => {
                const isCompleted = milestone.is_completed
                const isCurrent = !isCompleted && index === milestones.findIndex(m => !m.is_completed)
                
                return (
                  <div key={milestone.date} className="relative flex items-start pb-8 last:pb-0">
                    {/* Timeline Dot */}
                    <div className={cn(
                      "absolute left-4 w-8 h-8 rounded-full flex items-center justify-center -translate-x-1/2",
                      isCompleted ? "bg-green-100" : isCurrent ? "bg-blue-100" : "bg-gray-100"
                    )}>
                      <div className={cn(
                        "w-3 h-3 rounded-full",
                        isCompleted ? "bg-green-500" : isCurrent ? "bg-blue-500" : "bg-gray-400"
                      )} />
                    </div>
                    
                    {/* Content */}
                    <div className="ml-16 flex-1">
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <p className="font-medium">
                              {format(parseISO(milestone.date), 'MMMM d, yyyy')}
                            </p>
                            {milestone.is_cliff && (
                              <Badge variant="outline" className="text-xs">
                                <AlertCircle className="h-3 w-3 mr-1" />
                                Cliff
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm text-gray-600">
                            {formatShareNumber(milestone.shares_vesting)} shares vesting
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            {formatShareNumber(milestone.cumulative_vested_shares)} cumulative ({milestone.percentage_vested.toFixed(1)}%)
                          </p>
                        </div>
                        
                        {isCompleted && (
                          <CheckCircle2 className="h-5 w-5 text-green-500 mt-0.5" />
                        )}
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          ) : (
            /* Compact Year View */
            <div className="space-y-4">
              {Object.entries(milestonesByYear).map(([year, yearMilestones]) => {
                const yearCompleted = yearMilestones.every(m => m.is_completed)
                const yearShares = yearMilestones.reduce((sum, m) => sum + m.shares_vesting, 0)
                const lastMilestone = yearMilestones[yearMilestones.length - 1]
                
                return (
                  <div key={year} className="flex items-center justify-between p-3 rounded-lg bg-gray-50">
                    <div className="flex items-center gap-3">
                      <div className={cn(
                        "w-10 h-10 rounded-full flex items-center justify-center",
                        yearCompleted ? "bg-green-100" : "bg-blue-100"
                      )}>
                        <Calendar className={cn(
                          "h-5 w-5",
                          yearCompleted ? "text-green-600" : "text-blue-600"
                        )} />
                      </div>
                      <div>
                        <p className="font-medium">{year}</p>
                        <p className="text-sm text-gray-600">
                          {yearMilestones.length} vesting event{yearMilestones.length > 1 ? 's' : ''}
                        </p>
                      </div>
                    </div>
                    
                    <div className="text-right">
                      <p className="font-medium">{formatShareNumber(yearShares)} shares</p>
                      <p className="text-sm text-gray-500">
                        {lastMilestone.percentage_vested.toFixed(1)}% total
                      </p>
                    </div>
                    
                    {yearCompleted && (
                      <CheckCircle2 className="h-5 w-5 text-green-500 ml-3" />
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Vesting Details */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Vesting Details</CardTitle>
        </CardHeader>
        <CardContent>
          <dl className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <dt className="text-gray-500">Schedule</dt>
              <dd className="font-medium">{status.vesting_schedule.schedule_name}</dd>
            </div>
            <div>
              <dt className="text-gray-500">Duration</dt>
              <dd className="font-medium">{status.vesting_schedule.duration_months} months</dd>
            </div>
            <div>
              <dt className="text-gray-500">Cliff Period</dt>
              <dd className="font-medium">{status.vesting_schedule.cliff_months} months</dd>
            </div>
            <div>
              <dt className="text-gray-500">Frequency</dt>
              <dd className="font-medium capitalize">{status.vesting_schedule.vesting_frequency}</dd>
            </div>
            <div>
              <dt className="text-gray-500">Monthly Rate</dt>
              <dd className="font-medium">{formatShareNumber(Math.round(status.monthly_vesting_rate))} shares</dd>
            </div>
            <div>
              <dt className="text-gray-500">Vesting Start</dt>
              <dd className="font-medium">{format(parseISO(status.vesting_start_date), 'MMM d, yyyy')}</dd>
            </div>
          </dl>
        </CardContent>
      </Card>
    </div>
  )
}